import { toPng } from "html-to-image";
import JSZip from "jszip";
import React from "react";
import InvoicePreview from "./InvoicePreview";
import CouponBook from "../CouponBook";
import LandingPagePreview from "./LandingPagePreview";
import Modal from "./Modal";
import InvitePreview from "./InvitePreview";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const Layout = ({
    header, description,
    name, data, addData,
    handleDataChange, removeData,
    inputData, handleInputDataChange, optional = {},
    styles = ["minimal"]
}) => {
    const [collapsed, setCollapsed] = React.useState(data.map(() => true));
    const [html, setHtml] = React.useState("");
    const [isOpen, setIsOpen] = React.useState(false);
    const [buttonAction, setButtonAction] = React.useState({});
    const [buttonVal, setButtonVal] = React.useState({});
    const [primaryColorShades, setPrimaryColorShades] = React.useState({
        accent: "#c3deea",
        base: "#a8ccdc",
        softTint: "#eaede8",
        warmAccent: "#dceed3"
    });

    const toggleCollapse = (index) => {
        setCollapsed((prev) => {
            const newCollapsed = [...prev];
            newCollapsed[index] = !newCollapsed[index];
            return newCollapsed;
        });
    };

    async function recordDownloads() {
        try {
            await fetch(`${process.env.REACT_APP_API_URL}?route=${name.toLowerCase()}`);
        } catch (error) {
            console.error("Error:", error);
        }
    }

    const downloadInvoiceZip = async () => {
        const zip = new JSZip();

        zip.file("index.html", html);

        const blob = await zip.generateAsync({ type: "blob" });

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "landingPage.zip";
        a.click();

        URL.revokeObjectURL(url);
    };

    const downloadPng = async (id, title) => {
        const print_area = document.getElementById(id);
        if (!print_area) return;

        try {
            const dataUrl = await toPng(print_area, { useCORS: true, cacheBust: true });

            const link = document.createElement("a");
            link.href = dataUrl;
            link.download = title;
            link.click();
        } catch (error) {
            console.error("Error generating PNG:", error);
        }
    };

    const downloadPDF = async (id, file) => {
        const element = document.getElementById(id);

        if (!element) return;

        const canvas = await html2canvas(element, {
            scale: 2,
            backgroundColor: "#ffffff",
        });

        const imgData = canvas.toDataURL("image/png");

        const pdf = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "letter",
        });

        const margin = 15;
        const pageWidth = 216;
        const pageHeight = 279;

        const imgWidth = pageWidth - margin * 2;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let position = margin;
        let heightLeft = imgHeight;

        pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
        heightLeft -= pageHeight - margin * 2;

        while (heightLeft > 0) {
            pdf.addPage();
            position = margin - imgHeight + heightLeft;
            pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
            heightLeft -= pageHeight - margin * 2;
        }

        pdf.save(file);
    };

    async function handleDownload() {
        if (name === "LANDING PAGE") {
            downloadInvoiceZip();
        }
        else if (name === "INVITE") {
            let id = "invitation-print-area";
            let title = "invite.png";
            downloadPng(id, title);
        }
        else {
            let id = "print-area";
            let title = `${name.toLowerCase()}.pdf`
            switch (name) {
                case "INVOICE":
                    id = "invoice-print-area";
                    break;

                default:
                    break;
            }
            downloadPDF(id, title);
        }
        recordDownloads();
    }

    function toSentenceCase(str) {
        return str
            .replace(/_/g, " ")
            .replace(/\w\S*/g,
                (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
            );
    }

    function changeDataInput(k, v) {
        handleInputDataChange(k, v);
        if (k === "secondary_color" || k === "color") {
            const accents = generateBrandPalette(v);
            setPrimaryColorShades(accents);
        }
    }

    function changeData(i, k, v) {
        handleDataChange(i, k, v);
    }

    function generateBrandPalette(hex) {
        const { h, s, l } = hexToHsl(hex);
        return {
            base: hex,
            accent: hslToHex(h, clamp(s + 5), clamp(l + 8)),
            warmAccent: hslToHex(clamp(h - 20), clamp(s), clamp(l + 12)),
            softTint: hslToHex(clamp(h + 8), clamp(s - 30), 92),
        };
    }

    function hexToHsl(hex) {
        hex = hex.replace("#", "");
        const r = parseInt(hex.substring(0, 2), 16) / 255;
        const g = parseInt(hex.substring(2, 4), 16) / 255;
        const b = parseInt(hex.substring(4, 6), 16) / 255;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h, s;
        const l = (max + min) / 2;

        if (max === min) {
            h = s = 0;
        } else {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

            switch (max) {
                case r:
                    h = (g - b) / d + (g < b ? 6 : 0);
                    break;
                case g:
                    h = (b - r) / d + 2;
                    break;
                default:
                    h = (r - g) / d + 4;
                    break;
            }

            h *= 60;
        }

        return {
            h: Math.round(h),
            s: Math.round(s * 100),
            l: Math.round(l * 100),
        };
    }

    function hslToHex(h, s, l) {
        s /= 100;
        l /= 100;

        const k = n => (n + h / 30) % 12;
        const a = s * Math.min(l, 1 - l);
        const f = n =>
            l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

        return (
            "#" +
            [f(0), f(8), f(4)]
                .map(x => Math.round(x * 255).toString(16).padStart(2, "0"))
                .join("")
        );
    }

    function clamp(value, min = 0, max = 100) {
        return Math.min(max, Math.max(min, value));
    }

    function getFunctionObject(key) {
        switch (key) {
            case "Scroll To Section":
                return [{
                    "label": "Section to scroll to",
                    "value": "#" + data.map(p => p.title)[0].toLowerCase().replaceAll(" ", "-"),
                    "options": data.map(p => p.title).join(", ")
                }];
            case "Submit Form":
                return [{ "label": "email_address", "type": "text" }, { "label": "Occupation", "type": "options", "options": "Nurse, Firefighter" }];

            default:
                return [{ "label": "Destination URL", "value": "url or mailto:email" }];
        }
    }

    function handleButtonAction(val) {
        setIsOpen(true);
        const actionVal = inputData[val];
        const functionVal = val.replace("action", "function");
        let actionFunc = inputData[functionVal];
        if (actionVal !== buttonVal[val]) {
            const functionObject = getFunctionObject(actionVal);
            changeDataInput(functionVal, functionObject);
            actionFunc = functionObject;
            setButtonVal(prevData => ({
                ...prevData, [val]: actionVal
            }));
        }

        let title = "Redirect URL or Email Link";
        let description = "Enter the URL users would be redirected to, or a mailto link to open their email client (e.g., mailto:you@example.com).";

        if (actionVal.includes("Form")) {
            title = "Create A Form";
            description = "Add the information you'd like to collect. Choose a type (Text, Date, or Options), enter a field name, and if you select Options, provide a comma-separated list of choices.";
        }

        if (actionVal.includes("Section")) {
            title = "Scroll to Page Section";
            description = "Enter the title of the section you want users to scroll to when they click the button. Make sure the section exists on your page.";
        }

        setButtonAction({ action: functionVal, type: actionVal, actionFunc, title, description });
    }

    function closeModal() {
        setIsOpen(false);
        setButtonAction({});
    }

    function modalDataSave() {
        changeDataInput(buttonAction.action, buttonAction.actionFunc);
        closeModal();
    }

    function buttonDataOnChange(i, key, newValue) {
        setButtonAction((prev) => {
            const updatedActions = [...prev.actionFunc];

            updatedActions[i] = {
                ...updatedActions[i],
                [key]: newValue,
            };

            return {
                ...prev,
                actionFunc: updatedActions,
            };
        });
    }

    function addNewField() {
        setButtonAction((prev) => {
            const allActions = [...prev.actionFunc, {
                "label": "New Field", "type": "text"
            }];
            return {
                ...prev,
                actionFunc: allActions,
            };
        });
    }

    function removeField(i) {
        setButtonAction((prev) => {
            const allActions = [...prev.actionFunc];
            const remActions = allActions.filter((_, index) => index !== i);
            return {
                ...prev,
                actionFunc: remActions,
            };
        });
    }

    if (buttonAction.type) {
        return (
            <Modal
                isOpen={isOpen}
                onClose={closeModal}
            >
                <h2>{buttonAction.title}</h2>
                {buttonAction.type.includes("Form") && <div>
                    <p style={{ fontSize: 10 }}>
                        Where should we send your form responses?
                    </p>

                    <input
                        type="text"
                        placeholder="Paste your Form Action URL here"
                        value={inputData["WebhookURL"]}
                        className="form-control"
                        onChange={(e) => changeDataInput("WebhookURL", e.target.value)}
                    />
                    <p style={{ fontSize: 11, color: "#666" }}>
                        Get your Form Action URL from email marketing services like&nbsp;
                        <a href="https://mailchimp.com" target="_blank" rel="noreferrer">Mailchimp</a>,&nbsp;
                        <a href="https://app.kit.com" target="_blank" rel="noreferrer">Kit</a>, etc.<br />
                        When someone submits your form, their responses will be securely sent to this URL for processing or storage.
                    </p>
                </div>}
                <hr />
                <p style={{ fontSize: 10 }}>{buttonAction.description}</p>
                {buttonAction.actionFunc.map((a, i) => (
                    <div key={i}>
                        {buttonAction.type.includes("Section") ?
                            <div>
                                <select value={a.value}
                                    style={{ height: 40 }}
                                    className="mb-3 form-control"
                                    onChange={(e) => buttonDataOnChange(i, "value", e.target.value)}
                                >
                                    {a.options.split(", ").map(t => (
                                        <option key={t.toLowerCase()} value={"#" + t.toLowerCase().replaceAll(" ", "-")}>{t}</option>
                                    ))}
                                </select>
                            </div>
                            : a.type ?
                                <div className="input-group">
                                    <select value={a.type}
                                        style={{ height: 40 }}
                                        className="mb-3 form-control"
                                        onChange={(e) => buttonDataOnChange(i, "type", e.target.value)}
                                    >
                                        {["Text", "Date", "Options"].map(t => (
                                            <option key={t.toLowerCase()} value={t.toLowerCase()}>{t}</option>
                                        ))}
                                    </select>
                                    <input className="form-control"
                                        value={a.label || ""}
                                        style={{ height: 40 }}
                                        onChange={(e) => buttonDataOnChange(i, "label", e.target.value)}
                                    />
                                    {a.type === "options" &&
                                        <input className="form-control"
                                            value={a.options || ""}
                                            style={{ height: 40 }}
                                            onChange={(e) => buttonDataOnChange(i, "options", e.target.value)}
                                        />
                                    }
                                    {i === buttonAction.actionFunc.length - 1 && <button onClick={addNewField}>Add Field</button>}
                                    {i !== buttonAction.actionFunc.length - 1 && buttonAction.actionFunc.length !== 1 && <button onClick={() => removeField(i)}>Remove</button>}
                                </div> :
                                <div>
                                    <label>{a.label}</label>
                                    <input className="mb-3 form-control"
                                        value={a.value || ""}
                                        onChange={(e) => buttonDataOnChange(i, "value", e.target.value)}
                                    />
                                </div>
                        }
                    </div>
                ))}
                <button onClick={modalDataSave}>Save</button>
            </Modal>
        );
    }

    return (
        <div style={{ padding: "10px" }}>
            <h1 className="h1-title">{header}</h1>
            <section className="App-body">
                <div>
                    <p>{description}</p>
                </div>
                <div className="grid">
                    <div>
                        <table className="table table-bordered table-sm">
                            <tbody>
                                {Object.keys(inputData).filter(k => !k.includes("button_action") && !k.includes("WebhookURL") && !k.includes("button_function")).map((c, i) => (
                                    <tr key={c}>
                                        <td align="left">{toSentenceCase(c)}</td>
                                        <td>
                                            {c.includes("theme") ?
                                                <select value={inputData[c]} className="form-control" onChange={(e) => changeDataInput(c, e.target.value)}>
                                                    {styles.map(t => (
                                                        <option key={t}>{t}</option>
                                                    ))}
                                                </select>
                                                : <input
                                                    {...(c === "upload_image" && { accept: "image/*" })}
                                                    type={c.includes("color")
                                                        ? "color"
                                                        : (c.includes("handwritten") || c.includes("has_brand_logo"))
                                                            ? "checkbox"
                                                            : c.toLocaleLowerCase().includes("date")
                                                                ? "date"
                                                                : c === "upload_image"
                                                                    ? "file"
                                                                    : "text"}
                                                    className={(c === "handwritten" || c === "has_brand_logo") ? "form-check-input" : "form-control"}
                                                    defaultValue={inputData[c]}
                                                    onChange={(e) => (c === "handwritten" || c === "has_brand_logo")
                                                        ? changeDataInput(c, e.target.checked)
                                                        : c === "upload_image" ? changeDataInput(c, e.target.files[0])
                                                            : changeDataInput(c, e.target.value)}
                                                />}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <hr />
                        {name === "LANDING PAGE" &&
                            <div style={{ fontSize: 14 }}>
                                {Object.keys(inputData).filter(k => k.includes("button_action")).map((a, i) => (
                                    inputData[a.replace("action", "text")] && <div className="mb-3 input-group" key={i}>
                                        <label style={{ alignContent: "center" }}>{toSentenceCase(a)}: &nbsp;</label>
                                        <select value={inputData[a]}
                                            className="form-control" style={{ height: 40 }}
                                            onChange={(e) => changeDataInput(a, e.target.value)}>
                                            {["Submit Form", "Redirect to Url", "Scroll To Section"].map(t => (
                                                <option key={t}>{t}</option>
                                            ))}
                                        </select>
                                        <button onClick={() => handleButtonAction(a)}>SET</button>
                                    </div>
                                ))}
                                <hr />
                            </div>
                        }
                        <div style={{ padding: "0.2rem" }}>
                            {data.map((k, i) => (
                                <div key={i}>
                                    <div className="input-group">
                                        <input
                                            style={{ height: "40px" }}
                                            className="form-control mb-3"
                                            value={k.title}
                                            onChange={(e) => changeData(i, "title", e.target.value)}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => toggleCollapse(i)}
                                        >
                                            {collapsed[i] ? "+" : "-"}
                                        </button>
                                        {optional.createNew &&
                                            <button style={{ backgroundColor: "black" }} onClick={() => removeData(i)}>X</button>
                                        }
                                    </div>
                                    {!collapsed[i] && (
                                        Object.keys(k).filter(d => d !== "title").map((d, j) => (
                                            d === "features" ? (
                                                k.features.map((feature, l) => (
                                                    <div key={l} style={{ marginLeft: "1rem", marginBottom: "0.5rem" }}>
                                                        <div>
                                                            {Object.keys(feature).map((fKey) => (
                                                                <input
                                                                    key={fKey}
                                                                    className="form-control mb-1"
                                                                    value={feature[fKey]}
                                                                    placeholder={fKey}
                                                                    onChange={(e) => changeData(i, `features[${l}].${fKey}`, e.target.value)}
                                                                />
                                                            ))}
                                                        </div>
                                                        {k.features.length - 1 === l && <button style={{ backgroundColor: "orange" }} onClick={() => optional.addFeatureData(i)}>+</button>}
                                                        {k.features.length !== 1 && <button style={{ backgroundColor: "black" }} onClick={() => optional.removeFeatureData(i, l)}>X</button>}
                                                    </div>
                                                ))
                                            ) :
                                                d === "details" ? (
                                                    Object.keys(k.details).map((detail, l) => (
                                                        <div key={l} style={{ marginLeft: "1rem", marginBottom: "0.5rem" }}>
                                                            <div>
                                                                <input
                                                                    className="form-control mb-1"
                                                                    value={k.details[detail]}
                                                                    placeholder={detail}
                                                                    onChange={(e) => changeData(i, `features[${l}].${detail}`, e.target.value)}
                                                                />
                                                            </div>
                                                            {/* {k.features.length - 1 === l && <button style={{ backgroundColor: "orange" }} onClick={() => optional.addFeatureData(i)}>+</button>}
                                                            {k.features.length !== 1 && <button style={{ backgroundColor: "black" }} onClick={() => optional.removeFeatureData(i, l)}>X</button>} */}
                                                        </div>
                                                    ))
                                                ) : d === "flap_type" ? (
                                                    <select value={d}
                                                        style={{ height: 40 }}
                                                        className="mb-3 form-control"
                                                        onChange={(e) => buttonDataOnChange(i, "type", e.target.value)}
                                                    >
                                                        {optional.flap_types.map((t, i) => (
                                                            <option key={i} value={t}>{t}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <div key={j}>
                                                        <input
                                                            className="form-control mb-1"
                                                            value={k[d]}
                                                            type={["validUntil", "date"].includes(d) ? "date" : "text"}
                                                            placeholder={d}
                                                            onChange={(e) => changeData(i, d, e.target.value)}
                                                        />
                                                    </div>
                                                )))
                                    )}
                                </div>
                            ))}
                            {optional.createNew && <button style={{ width: "100%" }} onClick={addData}>ADD {optional.newButtonText ? optional.newButtonText : "NEW " + name}</button>}
                        </div>
                    </div>
                    <div className="preview">
                        <h3 style={{ textDecoration: "underline" }}>Preview</h3>
                        {name === "INVOICE" && <InvoicePreview mainData={inputData} invoiceData={data} totals={optional.totals} />}
                        {name === "COUPON" && <CouponBook coupons={data} couponData={inputData} />}
                        {name === "LANDING PAGE" && <LandingPagePreview pageData={data} inputData={inputData} colorTheme={primaryColorShades} html={html} setHtml={setHtml} />}
                        {name === "INVITE" && <InvitePreview inputData={inputData} colorTheme={primaryColorShades} />}
                        <button style={{ width: "100%" }} onClick={() => handleDownload()}>DOWNLOAD {name}</button>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default Layout;